import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardFooter,
  Divider,
  Flex,
  Heading,
  HStack,
  Image,
  Select,
  SimpleGrid,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tag,
  Text,
  useDisclosure,
  useToast,
  VStack,
  Wrap,
  WrapItem,
  Modal as ChakraModal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
} from "@chakra-ui/react";
import React from "react";
import DeleteModal from "../components/common/Modal/DeleteModal";
import PreviewModal from "../components/common/Modal/PreviewModal";
import CreateInput from "../components/common/TextInputs/Input";
import Fallback from "../components/common/ui/Fallback";
import Loading from "../components/common/ui/Loading";
import CustomModal from "../components/podcasts/Modal"; // Renamed to avoid conflict with Chakra's Modal
import {
  acceptUploadContentRequest,
  approvePayment,
  deleteUploadRequest,
  getSubCategories,
  getUploadContentRequest,
  rejectUploadContentRequest,
} from "../utils/api";
import useStore from "../utils/store";

const formatDateTime = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const isSeriesRequest = (item) =>
  item?.contentType === "Series" ||
  item?.type === "Multiple" ||
  (Array.isArray(item?.episodes) && item.episodes.length > 0);

const getRequestContentType = (item) =>
  isSeriesRequest(item) ? "Series" : "Movie";

const UploadRequest = () => {
  const [data, setData] = React.useState([]);
  const [selectedCategory, setSelectedCategory] = React.useState("All");
  const [selectedContentType, setSelectedContentType] = React.useState("All");
  const [loading, setLoading] = React.useState(false);
  const [categories, setCategories] = React.useState([]);

  const user = useStore((state) => state.user);

  const filteredData = React.useMemo(() => {
    return data.filter((item) => {
      const matchesCategory =
        selectedCategory === "All" || item.subcategory === selectedCategory;
      const matchesType =
        selectedContentType === "All" ||
        getRequestContentType(item) === selectedContentType;
      return matchesCategory && matchesType;
    });
  }, [data, selectedCategory, selectedContentType]);

  const getDataHandler = async () => {
    const [success, response] = await getUploadContentRequest();
    if (success) {
      setData(response);
    }
  };

  const getAllCategories = async () => {
    const [success, data] = await getSubCategories();
    if (success) {
      setCategories(data);
    }
  };

  React.useEffect(() => {
    if (user?.uploadAccess) {
      getDataHandler();
      getAllCategories();
    }
  }, [user]);

  if (loading) return <Loading />;

  return (
    <Box p={{ base: 4, md: 8 }} bg="gray.50" minH="100vh">
      {user?.uploadAccess ? (
        <Box maxW="1600px" mx="auto">
          {/* Header Section */}
          <Flex
            direction={{ base: "column", sm: "row" }}
            justify="space-between"
            align="center"
            mb={6}
            gap={4}
          >
            <Heading size="lg" color="gray.800">
              Upload Requests
            </Heading>
            <HStack w={{ base: "100%", sm: "auto" }} spacing={3}>
              <Select
                bg="white"
                w={{ base: "100%", sm: "180px" }}
                shadow="sm"
                onChange={(e) => setSelectedContentType(e.target.value)}
                value={selectedContentType}
              >
                <option value="All">All Types</option>
                <option value="Movie">Movie</option>
                <option value="Series">Series</option>
              </Select>
              <Select
                bg="white"
                w={{ base: "100%", sm: "250px" }}
                shadow="sm"
                onChange={(e) => setSelectedCategory(e.target.value)}
                value={selectedCategory}
              >
                <option value="All">All Categories</option>
                {categories?.map((item, index) => (
                  <option key={index} value={item.title}>
                    {item.title}
                  </option>
                ))}
              </Select>
            </HStack>
          </Flex>

          {/* Tabs Section */}
          <Box bg="white" shadow="sm" rounded="lg" p={4}>
            <Tabs variant="soft-rounded" colorScheme="teal">
              <TabList mb={4} overflowX="auto" pb={2}>
                <Tab>Pending</Tab>
                <Tab>Approved</Tab>
                <Tab>Permanent Reject</Tab>
                <Tab>Reject</Tab>
              </TabList>
              <TabPanels>
                {["Pending", "Approved", "Permanent Reject", "Reject"].map(
                  (tabType) => (
                    <TabPanel key={tabType} p={0}>
                      <TabsGrid
                        filteredData={filteredData}
                        type={tabType}
                        setLoading={setLoading}
                        getAllCategories={getAllCategories}
                        getDataHandler={getDataHandler}
                        categories={categories}
                      />
                    </TabPanel>
                  ),
                )}
              </TabPanels>
            </Tabs>
          </Box>
        </Box>
      ) : (
        <Fallback />
      )}
    </Box>
  );
};

export default UploadRequest;

// --- Sub Components ---

const TabsGrid = ({
  filteredData,
  setLoading,
  getAllCategories,
  type,
  getDataHandler,
  categories,
}) => {
  const toast = useToast();
  const [remark, setRemark] = React.useState("");

  // State for the Detail Modal
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedItem, setSelectedItem] = React.useState(null);

  const isActionable =
    type !== "Permanent Reject";

  const approvedActionable = type !== "Approved" && type !== "Permanent Reject";
  const rejectActionable = type !== "Reject" && type !== "Permanent Reject";
  const items = filteredData?.filter((item) => item.status === type) || [];

  const handleOpenDetails = (item) => {
    setSelectedItem(item);
    onOpen();
  };

  const handleApprovePayment = async (uploadId) => {
    setLoading(true);
    const [success, data] = await approvePayment(uploadId);
    if (success) {
      toast({
        title: "Payment Approved",
        status: "success",
        duration: 3000,
      });
      getDataHandler();
    } else {
      toast({
        title: "Error",
        description: data?.message,
        status: "error",
        duration: 5000,
      });
    }
    setLoading(false);
  };

  if (items.length === 0) {
    return (
      <Text textAlign="center" py={10} color="gray.500">
        No {type} requests found.
      </Text>
    );
  }

  return (
    <>
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 3, xl: 4 }} spacing={6} py={4}>
        {items.map((row) => (
          <Card
            key={row._id}
            shadow="md"
            borderWidth="1px"
            borderColor="gray.200"
            overflow="hidden"
            _hover={{
              shadow: "lg",
              transform: "translateY(-2px)",
              transition: "all 0.2s",
            }}
          >
            <Image
              src={row?.horizontalBanner}
              alt={row?.name}
              h="160px"
              w="100%"
              objectFit="cover"
              fallbackSrc="https://via.placeholder.com/300x160?text=No+Thumbnail"
            />
            <CardBody p={4}>
              <VStack align="start" spacing={2}>
                <HStack justify="space-between" w="100%" align="start">
                  <Heading size="md" noOfLines={1} title={row?.name}>
                    {row?.name || "Untitled"}
                  </Heading>
                  <HStack spacing={1} flexShrink={0}>
                    <Badge
                      colorScheme={
                        isSeriesRequest(row) ? "purple" : "blue"
                      }
                    >
                      {getRequestContentType(row)}
                    </Badge>
                    <Badge
                      colorScheme={
                        type === "Pending"
                          ? "orange"
                          : type === "Approved"
                            ? "green"
                            : "red"
                      }
                    >
                      {type}
                    </Badge>
                  </HStack>
                </HStack>
                <Text fontSize="sm" color="gray.500" noOfLines={2}>
                  {row?.description || "No description provided."}
                </Text>
                <HStack fontSize="xs" color="gray.500" spacing={4} pt={2}>
                  <Text>
                    {isSeriesRequest(row)
                      ? `🎬 ${row?.episodes?.length || 0} eps`
                      : `⏱ ${row?.duration || "N/A"}`}
                  </Text>
                  <Text>👤 {row?.user?.name || "Unknown"}</Text>
                  <Badge
                    colorScheme={
                      row?.paymentStatus === "Unpaid" ? "red" : "green"
                    }
                  >
                    {row?.paymentStatus || "N/A"}
                  </Badge>
                </HStack>
                <Text fontSize="xs" color="gray.500" pt={1}>
                  📅 {formatDateTime(row?.dateTime || row?.createdAt)}
                </Text>
                {
                  row?.ad && (<HStack fontSize="xs" color="gray.500" spacing={4} pt={2}>
                    <Badge colorScheme="purple" mt={2}>
                      {row.ad.name}
                    </Badge></HStack>
                  )
                }
              </VStack>
            </CardBody>

            <Divider />

            <CardFooter p={3} bg="gray.50" flexDir="column" gap={3}>
              <Button
                size="sm"
                colorScheme="teal"
                variant="solid"
                w="100%"
                onClick={() => handleOpenDetails(row)}
              >
                View Details
              </Button>

              {row?.paymentStatus === "Unpaid" && (
                <Button
                  size="sm"
                  colorScheme="blue"
                  variant="outline"
                  w="100%"
                  onClick={() => handleApprovePayment(row._id)}
                >
                  Approve Payment
                </Button>
              )}

              {/* Quick Actions Grid (2x2 Layout to prevent clutter) */}
              {isActionable && (
                <SimpleGrid columns={2} spacing={2} w="100%">
                  {/* 1. Approve */}
                  {approvedActionable && (<CustomModal
                    buttonText="Approve"
                    update={row}
                    series={isSeriesRequest(row)}
                    save={async (res) => {
                      const seriesRequest = isSeriesRequest(row);
                      const episodes = seriesRequest
                        ? res?.episodes || row?.episodes || []
                        : undefined;
                      if (
                        seriesRequest &&
                        !episodes?.some((episode) => episode?.url)
                      ) {
                        toast({
                          title: "Error",
                          description:
                            "This series request has no episodes with a video URL.",
                          status: "error",
                          duration: 5000,
                        });
                        return;
                      }
                      setLoading(true);
                      const [success, data] = await acceptUploadContentRequest({
                        ...res,
                        uploadedBy: row?.user?._id,
                        requestId: row?._id,
                        contentType: seriesRequest ? "Series" : "Movie",
                        type: seriesRequest ? "Multiple" : "Single",
                        episodes,
                      });
                      if (success) {
                        toast({
                          title: "Approved",
                          status: "success",
                          duration: 3000,
                        });
                        getDataHandler();
                      } else {
                        toast({
                          title: "Error",
                          description: data?.message,
                          status: "error",
                          duration: 5000,
                        });
                      }
                      setLoading(false);
                    }}
                    title="Approve"
                    color="green"
                    categories={categories}
                  />)}

                  {/* 2. Normal Reject */}
                  {
                    rejectActionable && (<DeleteModal
                      reject="Reject"
                      onClick={async () => {
                        setLoading(true);
                        const [success, data] = await rejectUploadContentRequest({
                          requestId: row._id,
                          status: "Reject",
                          remark,
                        });
                        if (success) {
                          toast({
                            title: "Rejected",
                            status: "success",
                            duration: 3000,
                          });
                          setRemark("");
                          getDataHandler();
                        } else {
                          toast({
                            title: "Error",
                            description: data?.message,
                            status: "error",
                            duration: 5000,
                          });
                        }
                        setLoading(false);
                      }}
                    >
                      <CreateInput
                        name="Remark"
                        onChange={(e) => setRemark(e.target.value)}
                        value={remark}
                      />
                    </DeleteModal>)
                  }

                  {/* 3. Permanent Reject */}
                  <DeleteModal
                    reject="Perm. Reject" // Shortened slightly to fit better in the grid
                    onClick={async () => {
                      setLoading(true);
                      const [success, data] = await rejectUploadContentRequest({
                        requestId: row._id,
                        status: "Permanent Reject",
                        remark,
                      });
                      if (success) {
                        toast({
                          title: "Permanently Rejected",
                          status: "success",
                          duration: 3000,
                        });
                        setRemark("");
                        getDataHandler();
                      } else {
                        toast({
                          title: "Error",
                          description: data?.message,
                          status: "error",
                          duration: 5000,
                        });
                      }
                      setLoading(false);
                    }}
                  >
                    <CreateInput
                      name="Remark"
                      onChange={(e) => setRemark(e.target.value)}
                      value={remark}
                    />
                  </DeleteModal>

                  {/* 4. Hard Delete */}
                  <DeleteModal
                    onClick={async () => {
                      setLoading(true);
                      const [success, data] = await deleteUploadRequest(
                        row._id,
                      );
                      if (success) {
                        toast({
                          title: "Deleted",
                          status: "success",
                          duration: 3000,
                        });
                        getAllCategories();
                        getDataHandler();
                      } else {
                        toast({
                          title: "Error",
                          description: data?.message,
                          status: "error",
                          duration: 5000,
                        });
                      }
                      setLoading(false);
                    }}
                  />
                </SimpleGrid>
              )}
            </CardFooter>
          </Card>
        ))}
      </SimpleGrid>

      {/* Details Popup Modal */}
      <ChakraModal
        isOpen={isOpen}
        onClose={onClose}
        size="3xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottomWidth="1px">
            Request Details
            {selectedItem && (
              <Badge
                ml={3}
                colorScheme={isSeriesRequest(selectedItem) ? "purple" : "blue"}
              >
                {getRequestContentType(selectedItem)}
              </Badge>
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody p={6}>
            {selectedItem && (
              <Sheet
                data={selectedItem}
                onApprovePayment={handleApprovePayment}
              />
            )}
          </ModalBody>
        </ModalContent>
      </ChakraModal>
    </>
  );
};

// --- Sheet Component (Rendered inside the Modal) ---
const Sheet = ({ data, onApprovePayment }) => {
  const {
    isOpen: isPreviewOpen,
    onOpen: onPreviewOpen,
    onClose: onPreviewClose,
  } = useDisclosure();
  const [previewUrl, setPreviewUrl] = React.useState("");
  const [previewTitle, setPreviewTitle] = React.useState("Preview");
  const [previewUrlType, setPreviewUrlType] = React.useState("");

  const handlePreview = (url, title, urlType) => {
    setPreviewUrl(url);
    setPreviewTitle(title);
    setPreviewUrlType(urlType);
    onPreviewOpen();
  };

  return (
    <VStack align="stretch" spacing={6}>
      <PreviewModal
        isOpen={isPreviewOpen}
        onClose={onPreviewClose}
        url={previewUrl}
        title={previewTitle}
        urlType={previewUrlType}
      />

      <Box>
        <Text fontWeight="bold" color="gray.700" mb={2}>
          Description
        </Text>
        <Text color="gray.600" fontSize="sm">
          {data?.description || "No description provided."}
        </Text>
      </Box>

      <Divider />

      <Box>
        <Text fontWeight="bold" color="gray.700" mb={4}>
          Media
        </Text>
        <HStack spacing={4} align="start">
          <VStack w="50%">
            <Image
              src={data?.horizontalBanner}
              alt="Horizontal Banner"
              w="100%"
              aspectRatio="16/9"
              objectFit="cover"
              rounded="md"
              border="1px solid"
              borderColor="gray.200"
              fallbackSrc="https://via.placeholder.com/300x170?text=No+Banner"
            />
            <Button
              size="sm"
              w="100%"
              colorScheme="blue"
              variant="outline"
              onClick={() =>
                handlePreview(
                  data?.horizontalBanner,
                  "Horizontal Banner",
                  "image",
                )
              }
            >
              Preview Horizontal
            </Button>
          </VStack>
          <VStack w="50%">
            <Image
              src={data?.verticalBanner}
              alt="Vertical Banner"
              w="100%"
              aspectRatio="16/9"
              objectFit="cover"
              rounded="md"
              border="1px solid"
              borderColor="gray.200"
              fallbackSrc="https://via.placeholder.com/170x300?text=No+Banner"
            />
            <Button
              size="sm"
              w="100%"
              colorScheme="blue"
              variant="outline"
              onClick={() =>
                handlePreview(data?.verticalBanner, "Vertical Banner", "image")
              }
            >
              Preview Vertical
            </Button>
          </VStack>
        </HStack>
      </Box>

      <Divider />

      <Box>
        <Text fontWeight="bold" color="gray.700" mb={4}>
          Specifications
        </Text>
        <SimpleGrid columns={{ base: 1, sm: 2 }} spacingX={8} spacingY={4}>
          <DetailRow
            label="Type"
            value={getRequestContentType(data)}
          />
          <DetailRow
            label="Date & Time"
            value={formatDateTime(data?.dateTime || data?.createdAt)}
          />
          <DetailRow
            label="Duration"
            value={
              isSeriesRequest(data)
                ? `${data?.episodes?.length || 0} episodes${
                    data?.duration ? ` (${data.duration})` : ""
                  }`
                : data?.duration
            }
          />
          <DetailRow label="Age Limit" value={data?.ageLimit} />
          <DetailRow label="Rating" value={data?.rating} />
          <DetailRow label="Amount" value={`₹${data?.amount || 0}`} />
          <DetailRow
            label="Trailer"
            value={
              data?.trailer ? (
                <Button
                  size="xs"
                  colorScheme="teal"
                  onClick={() =>
                    handlePreview(data?.trailer, "Trailer", data?.trailerType)
                  }
                >
                  Preview Trailer
                </Button>
              ) : (
                "N/A"
              )
            }
          />
          {!isSeriesRequest(data) && (
            <DetailRow
              label="Video"
              value={
                data?.url ? (
                  <Button
                    size="xs"
                    colorScheme="teal"
                    onClick={() =>
                      handlePreview(data?.url, "Video", data?.urlType)
                    }
                  >
                    Watch Video
                  </Button>
                ) : (
                  "N/A"
                )
              }
            />
          )}
        </SimpleGrid>
      </Box>

      {isSeriesRequest(data) && (
        <>
          <Divider />
          <Box>
            <Text fontWeight="bold" color="gray.700" mb={4}>
              Episodes ({data?.episodes?.length || 0})
            </Text>
            {data?.episodes?.length > 0 ? (
              <VStack align="stretch" spacing={3}>
                {data.episodes.map((episode, index) => (
                  <Box
                    key={episode._id || index}
                    borderWidth="1px"
                    borderColor="gray.200"
                    rounded="md"
                    p={3}
                    bg="gray.50"
                  >
                    <HStack justify="space-between" align="start" spacing={3}>
                      <VStack align="start" spacing={1} flex={1}>
                        <Text fontWeight="600" fontSize="sm">
                          {episode.seasonNumber
                            ? `S${episode.seasonNumber}E${
                                episode.episodeNumber || index + 1
                              } · `
                            : `Episode ${episode.episodeNumber || index + 1} · `}
                          {episode.name || "Untitled episode"}
                        </Text>
                        {episode.description && (
                          <Text fontSize="xs" color="gray.500" noOfLines={2}>
                            {episode.description}
                          </Text>
                        )}
                        <Text fontSize="xs" color="gray.500">
                          {episode.duration || "Duration N/A"}
                        </Text>
                      </VStack>
                      {episode.url && (
                        <Button
                          size="xs"
                          colorScheme="teal"
                          onClick={() =>
                            handlePreview(
                              episode.url,
                              episode.name || `Episode ${index + 1}`,
                              episode.urlType || "Video",
                            )
                          }
                        >
                          Watch
                        </Button>
                      )}
                    </HStack>
                  </Box>
                ))}
              </VStack>
            ) : (
              <Text fontSize="sm" color="gray.500">
                No episodes were submitted with this series request.
              </Text>
            )}
          </Box>
        </>
      )}

      <Divider />

      <Box>
        <Text fontWeight="bold" color="gray.700" mb={4}>
          Cast & Categories
        </Text>
        <VStack align="stretch" spacing={3}>
          <Box>
            <Text fontSize="sm" color="gray.500" mb={1}>
              Cast
            </Text>
            <Text fontSize="sm">
              {data?.cast
                ?.map((item) => `${item?.name} (${item?.role})`)
                .join(", ") || "N/A"}
            </Text>
          </Box>
          <Box>
            <Text fontSize="sm" color="gray.500" mb={1}>
              Extra Cast
            </Text>
            <Text fontSize="sm">{data?.extraCast || "N/A"}</Text>
          </Box>
          <Box>
            <Text fontSize="sm" color="gray.500" mb={1}>
              Genres
            </Text>
            <Wrap>
              {data?.genres?.map((item, index) => (
                <WrapItem key={index}>
                  <Tag size="sm" colorScheme="blue" borderRadius="full">
                    {item.name}
                  </Tag>
                </WrapItem>
              ))}
            </Wrap>
          </Box>
        </VStack>
      </Box>

      <Divider />

      <Box>
        <Text fontWeight="bold" color="gray.700" mb={4}>
          Uploader Information
        </Text>
        <VStack align="stretch" spacing={2} bg="gray.50" p={4} rounded="md">
          <DetailRow label="User Name" value={data?.user?.name} />
          <DetailRow label="Email" value={data?.user?.email} />
          <DetailRow label="Phone" value={data?.user?.phoneNumber} />
          {data?.remark && <DetailRow label="Remark" value={data?.remark} />}
          <Box>
            <Text fontWeight="bold" color="gray.700" mb={4}>
              Payment Information
            </Text>
            <VStack align="stretch" spacing={2} bg="gray.50" p={4} rounded="md">
              <DetailRow
                label="Payment Status"
                value={
                  <Badge
                    colorScheme={
                      data?.paymentStatus === "Unpaid" ? "red" : "green"
                    }
                  >
                    {data?.paymentStatus || "Not Paid"}
                  </Badge>
                }
              />
              {data?.paymentStatus === "Unpaid" && onApprovePayment && (
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={() => onApprovePayment(data._id)}
                >
                  Approve Payment
                </Button>
              )}
            </VStack>
          </Box>
        </VStack>
      </Box>
    </VStack>
  );
};

// --- Reusable Detail Row Component ---
const DetailRow = ({ label, value }) => (
  <Flex
    justify="space-between"
    align="center"
    py={1}
    borderBottom="1px dashed"
    borderColor="gray.200"
  >
    <Text fontSize="sm" color="gray.500" fontWeight="500">
      {label}
    </Text>
    {typeof value === "string" || typeof value === "number" ? (
      <Text fontSize="sm" fontWeight="600" color="gray.800" textAlign="right">
        {value || "-"}
      </Text>
    ) : (
      value
    )}
  </Flex>
);
